import { format, parseISO } from 'date-fns';
import { type Locale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/Icon';
import { WEATHER_TONE, weatherCondition } from '../../../utils/weatherSymbol';
import type { DayWeather } from '../../../api/weather.api';

interface Props {
  weather: DayWeather;
  /** `compact` fits the day strip; `full` goes in a date header. */
  variant?: 'compact' | 'full';
  locale?: Locale;
  /**
   * Opens the hour-by-hour panel. Without it the chip stays the plain `<span>`
   * it has always been — it is rendered inside the day-strip `<button>`, and a
   * button inside a button is invalid HTML that React will warn about.
   */
  onClick?: () => void;
}

/**
 * ADR-0006: a 30-year monthly average is never dressed up as a forecast. When
 * the source is `climate` the chip drops the condition icon — we know the usual
 * temperature of that month, not whether it will be sunny — prefixes the range
 * with `~`, and says which month it is talking about.
 */
export function DayWeatherChip({ weather, variant = 'compact', locale, onClick }: Props) {
  const { t } = useTranslation();
  const { tempMax, tempMin, source } = weather;
  if (tempMax === null && tempMin === null) return null;

  const condition = source === 'forecast' ? weatherCondition(weather.symbol) : null;
  const month = format(parseISO(`${weather.date}T00:00:00`), 'LLLL', { locale });
  const approx = source === 'climate' ? '~' : '';

  const label = condition ? t(`itinerary.weather.condition.${condition.labelKey}`) : '';
  const reading = source === 'forecast'
    ? t('itinerary.weather.aria', { condition: label, max: tempMax, min: tempMin })
    : t('itinerary.weather.ariaTypical', { month, max: tempMax, min: tempMin });

  // The chip has no room to print where the reading comes from, but a number
  // whose location you cannot find out is a number you cannot check. The panel
  // shows it; here it lives in the tooltip and in the accessible name.
  const where = weather.place
    ? t(weather.place.from === 'trip' ? 'itinerary.weather.atTrip' : 'itinerary.weather.at', { place: weather.place.name })
    : null;
  const aria = where ? `${reading} · ${where}` : reading;

  if (variant === 'compact') {
    return (
      <span className="flex items-center gap-0.5 leading-none" title={aria} aria-label={aria}>
        {condition && <Icon as={condition.icon} size={11} className="opacity-70" />}
        <span className="font-mono text-[10px] tabular-nums opacity-80">{approx}{tempMax}°</span>
      </span>
    );
  }

  const Tag = onClick ? 'button' : 'span';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={onClick ? t('itinerary.weather.panel.open') : undefined}
      className={`inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-600 dark:text-slate-300${
        onClick ? ' cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors' : ''
      }`}
      aria-label={onClick ? `${aria} — ${t('itinerary.weather.panel.open')}` : aria}
    >
      {condition && <Icon as={condition.icon} size={13} className={WEATHER_TONE[condition.tone].icon} />}
      {/* One `~` in front of the whole range, never one per number: with a
          negative low it renders as "~-6°", which reads as a dash, not as an
          approximation. */}
      <span className="font-mono text-[11px] tabular-nums">
        {approx}{tempMax}° / {tempMin}°
      </span>
      {source === 'climate' && (
        <span className="text-[10px] text-slate-400 dark:text-slate-500">
          {t('itinerary.weather.typicalFor', { month })}
        </span>
      )}
    </Tag>
  );
}
